import { IResponsePagination } from "./pagination.types";

export enum EVENT_PRODUCT_TYPE_E {
	RECON = 0,
	TT = 1,
	VIDS = 2
}

export interface IGetAlertEventsRequestData {
	product_type: EVENT_PRODUCT_TYPE_E;
	timestamp__gte?: string;
	timestamp__lte?: string;
	page: number;
	event_type?: "alerts";
	search?: string;
	violation_ids?: number[];
	camera?: number;
	device?: number[];
	vehicle_class?: number;
	vehicle_category?: number;
	color?: number;
	speed_range?: string;
	radar_speed_range?: string;
	vehicle_brand?: number;
	vehicle_type?: number;
	vehicle_model?: number;
	direction?: number;
	is_bookmarked?: boolean;
	ai_meta_data?: string;
	session?: number;
	page_size?: number;
	validated_status?: EventValidationStatusType;
	number_plate_exact?: string;
	challan_status?: EventChallanStatusType;
}

export interface IAlertViolationDetails {
	lane: number;
	lane_name: string;
	violation: number | null;
	violation_name: string;
}

export interface IEventCameraROIDetails {
	id: number;
	created_at: string;
	updated_at: string;
	ignore: boolean;
	camera_roi_uuid: string;
	name: string;
	line: string;
	roi: string;
	num_lines: number;
	camera_id: number;
	enable_speed_analytics: boolean;
	starting_line: number[];
	ending_line: number[];
	path_length: number;
	path_width: number;
	radar_speed_display_config: {
		lane_range: number[];
		speed_display: {
			host: string;
			port: number;
			enable: boolean;
			display_secs: number;
		};
		radar_lane_map: string;
	};
}

export interface IEventCameraDetails {
	id: number;
	camera_config_roi: IEventCameraROIDetails[];
	created_at: string;
	updated_at: string;
	ignore: boolean;
	camera_uuid: string;
	rtsp_uri: string;
	site_name: string;
	rtmp_uri: string | null;
	roi: string;
	line: string;
	zoom: number | null;
	is_entry: boolean;
	width: number;
	height: number;
	num_lines: number;
	site_id: string;
	camera_id: string;
	latest_camera_snapshot: string | null;
	camera_page_url: string | null;
	secondary_stream_rtsp_uri: string | null;
	is_camera_snapshot_synced: boolean;
	device: number;
	customer: null;
}

export interface IEventViolationDetails {
	id: number;
	violation: number;
	violation_name: string;
	lane: number;
	lane_name: string;
}

export interface IEventDeviceMLSettings {
	radar_info: {
		host: string;
		port: number;
		enable: boolean;
		y_range: number[];
	};
	relay_setting: number;
	allow_unauth_exit: boolean;
	draw_vehicle_bbox: boolean;
	manager_webhook_url: string;
	show_lc_counts_rtmp: boolean;
	draw_number_plate_bbox: boolean;
	enable_manager_webhook: boolean;
	show_processing_fps_rtmp: boolean;
	enable_auth_plate_mathching: boolean;
	manager_webhook_attach_frame: boolean;
	manager_webhook_attach_plate: boolean;
	auth_plate_matching_threshold: number;
	manager_webhook_custom_header: {};
	manager_webhook_send_single_event: boolean;
}

export interface IEventDeviceHealthData {
	device_data: {
		detail: [
			{
				device_uptime: {
					Days: number;
					Hours: number;
					Minutes: number;
					Seconds: number;
				};
			},
			{
				GPU: [number, number];
				Thermal: [number, number];
			},
			{
				Ram: [number, number];
				Swap: [number, number];
				Device: [number, number];
				External: [number, number];
			}
		];
		status: string;
		message: string[];
	};
	docker_data: {
		detail: {
			[key in string]: [string, string, string];
		};
		status: string;
		message: string[];
	};
}

export interface IEventDeviceDetails {
	id: number;
	device_name: string;
	username: string | null;
	drop_frame: number;
	batch_timeout: number;
	barrier_uptime: number;
	device_uuid: string;
	release_version: string;
	local_ipv4_address: string;
	global_ipv4_address: string;
	mode: number;
	device_type: string;
	light_activation_time: number;
	barrier_config: number;
	si_id: number | null;
	blob_base_url: string | null;
	plate_img_base_url: string | null;
	frame_img_base_url: string | null;
	camera_img_base_url: string | null;
	product_name: string;
	camera_mode: number;
	filter_unreadable_plate_events: boolean;
	overspeeding_limit_kmph: number;
	overspeeding_alert_email: string | null;
	ml_settings: IEventDeviceMLSettings;
	product_type: number;
	device_status: "Online" | "Offline";
	health_data: IEventDeviceHealthData;
	customer: null;
}

export interface IEvidenceImageURLDetails {
	url: string;
	id: number;
	file_name?: string;
	ignore: boolean;
	evidence_type: "FRAME" | "EVIDENCE_IMAGE";
}

export type EventValidationStatusType = "pending" | "reject" | "approved";
export type EventChallanStatusType = "created" | "draft" | "issued" | "paid" | "cancelled" | "failed";

export interface IGetAlertEventsResponseDataOld {
	id: number;
	vehicle_type: number;
	auth: boolean;
	vehicle_class: string;
	color: string;
	speed: number | null;
	timestamp: string;
	number_plate: string;
	camera: IEventCameraDetails;
	site_name: string;
	unique_id: string;
	ignore: boolean;
	vehicle_brand: string | null;
	vehicle_brand_icon: string | null;
	color_icon: string;
	direction: number;
	direction_name: string;
	violations: IEventViolationDetails[];
	radar_speed: number | null;
	evidence_video_url: string;
	bbox: [number, number, number, number][];
	vehicle_model: string | null;
	device: IEventDeviceDetails;
	vehicle_class_icon: string;
	vehicle_category: string | null;
	is_bookmarked: boolean;
	ai_meta_data: string | null;
	is_validated: boolean;
	gantry_average_speed: number | null;
	is_blacklisted: boolean;
	crop_co_ordinates: {
		plate: number[];
		violation: number[][];
		plate_image_name?: string;
		violation_image_name?: string;
	};
	officer_name: string;
	location_name: string;
	location_co_ordinates: string;
	evidence_images_count: number;
	evidence_images: string[];
}

export interface IEventChallanDetails {
	id: number;
	transaction_no: string;
	status: string;
	violation_type: string;
	offence_id: string;
	offence_name: string;
	created_at: string;
	action_time: string;
}

export interface IEventChallanInfo {
	challans: IEventChallanDetails[];
	draft_count: number;
	failed_count: number;
	issued_count: number;
	total_count: number;
}

export interface IGetAlertEventsResponseData {
	id: number;
	vehicle_type: number;
	auth: boolean;
	vehicle_class: string;
	color: string;
	speed: number | null;
	timestamp: string;
	number_plate: string;
	camera: IEventCameraDetails;
	site_name: string;
	created_at: string;
	unique_id: string;
	ignore: boolean;
	updated_at: string;
	vehicle_brand: string;
	vehicle_brand_icon: string;
	color_icon: string;
	direction: number;
	direction_name: "APPROACHING" | "DEPARTING";
	violations: IEventViolationDetails[];
	radar_speed: number | null;
	evidence_video_url: string;
	bbox: [number, number, number, number][];
	vehicle_model: string;
	device: IEventDeviceDetails;
	vehicle_class_icon: string;
	vehicle_category: string;
	is_bookmarked: boolean;
	ai_meta_data: string | null;
	is_validated: boolean;
	gantry_average_speed: number | null;
	is_blacklisted: boolean;
	crop_co_ordinates: {
		plate: number[];
		violation: [number, number, number, number][];
		plate_image_name?: string;
		violation_image_name?: string;
	};
	officer_name: string;
	location_name: string;
	location_co_ordinates: string;
	evidence_images_count: number;
	evidence_images: string[];
	frame_evidences: string[];
	plate_evidences: string[];
	new_evidence_images: IEvidenceImageURLDetails[];
	violation_evidences: string[];
	validated_at: string | null;
	interceptor: number;
	validated_status: EventValidationStatusType;
	comment: string | null;
	challan_info: IEventChallanInfo;
}

export type IGetAlertEventsResponse = IResponsePagination<IGetAlertEventsResponseData>;

export interface IDownloadLogsRequestData {
	start_date: string;
	end_date: string;
	product_type?: EVENT_PRODUCT_TYPE_E;
	session?: number;
	search?: string;
	violation_ids?: number[];
	camera?: number;
	device?: number[];
	vehicle_class?: number;
	vehicle_category?: number;
	color?: number;
	speed_range?: string;
	radar_speed_range?: string;
	vehicle_brand?: number;
	vehicle_type?: number;
	vehicle_model?: number;
	direction?: number;
	validated_status?: EventValidationStatusType;
}

export enum EVENT_DIRECTION_E {
	"DEPARTING" = 0,
	"APPROACHING" = 1
}

export interface IValidateViolationDetails {
	violation_id: number;
	lane_id: number | null;
}

export interface IValidateViolationsRequestData {
	event_id: number;
	violations: IValidateViolationDetails[];
}

export interface IEditEventBookmark {
	event_id: string;
	data: {
		is_bookmarked: boolean;
	};
}

export interface IGetImageURLRequestData {
	filename: string;
	filetype: "plates" | "frame" | "vids_recordings";
}

export interface IGetImageURLResponseData {
	url: string;
}

export interface IGetAIEventsRequestData {
	timestamp__gte?: string;
	timestamp__lte?: string;
	page_number: number;
	top_k: number;
	pagination_key?: string;
	device_id?: number[];
	camera_id?: number;
	search: string;
	accuracy: number;
	n_probe: number;
	session?: number;
}

export interface IGetAIEventsResponse {
	count: number;
	pagination_key: string;
	results: IGetAlertEventsResponseData[];
}

export interface IEnforcementSessionOperator {
	id: number;
	name: string;
	interceptor_id: string;
	location_name: string;
	is_active: boolean;
}

export interface IGetEnforcementSessionsResponseData {
	id: number;
	is_ongoing: boolean;
	officer_username: string;
	interceptor_numberplate: string;
	created_at: string;
	uuid: string;
	session_start: string;
	session_end: string;
	latitude: number;
	longitude: number;
	officer: number;
	interceptor: number;
	status: "NOT VALIDATED" | "SYNCED" | "SYNC PENDING";
	location_name: string;
	officer_first_name: string;
	officer_last_name: string;
	validated_status: "pending" | "approved" | "rejected";
}

export interface IGetEnforcementSessionsRequestData {
	page: number;
	/** Format for session start date: YYYY-MM-DDTHH:mm:ss */
	session_start__gt: string;
	/** Format for session end date: YYYY-MM-DDTHH:mm:ss */
	session_start__lt: string;
	page_size?: number;
	validated_status?: "pending" | "approved" | "rejected";
	device?: number[];
	search?: string;
}

export type IGetEnforcementSessionsResponse = IResponsePagination<IGetEnforcementSessionsResponseData>;

export interface IGetVehicleLogsResponseData {
	id: number;
	vehicle_type: number;
	auth: boolean;
	vehicle_class: number | null;
	color: number | null;
	speed: number | null;
	radar_speed: number | null;
	timestamp: string;
	number_plate: string;
	camera: number;
	site_name: string;
	unique_id: string;
	vehicle_brand: number | null;
	vehicle_brand_icon: string | null;
	vehicle_model: number | null;
	plate_image_url: string;
	color_icon: string | null;
	direction: 0 | 1;
	direction_name: "DEPARTING" | "APPROACHING";
	violations: IEventViolationDetails[];
	vehicle_category: number | null;
	bbox: [number, number, number, number][];
	evidence_video_url: string;
	vehicle_model_name: string | null;
	color_name: string | null;
	vehicle_class_name: string | null;
	vehicle_brand_name: string | null;
	vehicle_category_name: string | null;
	// evidence_image_url: { image: string; ai_meta_data?: string | null }[];
	evidence_image_url: IEvidenceImageURLDetails[];
	ai_meta_data?: string | null;
	crop_co_ordinates: {
		plate: number[];
		violation: number[][];
		plate_image_name?: string;
		violation_image_name?: string;
	};
	enforcement_session: number;
	enforment_verify_status: EventValidationStatusType;
	location_co_ordinates: string;
	location_name: string;
	officer_name: string;
	violation_image_url: string[];
	is_synced: boolean;
}

export interface IUpdateVehicleEventRequestData {
	unique_id: string;
	data: {
		number_plate?: string;
		vehicle_type?: number;
		vehicle_class?: number;
		vehicle_brand?: number;
		vehicle_model?: number;
		vehicle_category?: number;
		color?: number;
		speed?: number;
		radar_speed?: number;
		direction?: EVENT_DIRECTION_E;
		crop_co_ordinates?: {
			plate: number[];
			violation: number[][];
			plate_image_name?: string;
			violation_image_name?: string;
		};
		validated_status?: EventValidationStatusType;
		violations?: IEventViolationDetails[];
		comment?: string;
	};
}

export interface IUpdateEvidenceImageRequestData {
	id: number;
	data: {
		ignore: boolean;
	};
}

export interface IGetVehicleDetailsRequestData {
	number_plate: string;
	force_refresh?: boolean;
}

export interface IVehicleDetails {
	number_plate: string;
	owner_name: string;
	registration_date: string;
	registration_valid_upto: string;
	vehicle_class: string;
	maker_description: string;
	maker_model: string;
	fuel_type: string;
	color: string;
	present_address: string;
	permanent_address: string;
	chassis_number: string;
	engine_number: string;
	seating_capacity: number;
	insurance_company: string;
	insurance_valid_upto: string;
	fitness_valid_upto: string | null;
	tax_valid_upto: string;
	pucc_number: string;
	pucc_valid_upto: string;
	vehicle_status: string;
	ncrb_status: string;
	blacklist_status: string;
	state_code: string;
	rto_code: string;
	registered_at: string;
	api_status: string;
	api_response_message: string;
	fetched_at: string;
	is_insurance_valid: boolean;
	rc_mobile_no: string;
}

export interface IChallanViolationDetails {
	violation_type: string;
	violation_name: string;
	offence_id: string;
	offence_name: string;
	can_create_challan: boolean;
	is_additional: boolean;
	error?: string;
}

export interface IPreviewChallanDetails {
	event_id: number;
	number_plate: string;
	vehicle_details: {
		stautsMessage: string;
		rc_regn_no: string;
		rc_regn_dt: string;
		rc_regn_upto: string;
		rc_owner_name: string;
		state_cd: string;
		rto_cd: string;
		rc_present_address: string;
		rc_permanent_address: string;
		rc_mobile_no: string;
		rc_vh_class_desc: string;
		rc_chasi_no: string;
		rc_eng_no: string;
		rc_maker_desc: string;
		rc_maker_model: string;
		rc_fuel_desc: string;
		rc_color: string;
		rc_fit_upto: string;
		rc_tax_upto: string;
		rc_insurance_comp: string;
		rc_insurance_upto: string;
		rc_seat_cap: string;
		rc_registered_at: string;
		rc_status: string;
		rc_blacklist_status: string | null;
		rc_vh_class: string;
		rc_fuel_cd: string;
		rc_maker_cd: string;
		rc_norms_cd: string;
		rc_sale_amt: string;
		rc_vehicle_surrendered_to_dealer: string;
		rc_currentadd_statename: string;
		rc_currentadd_districtcode: string;
		rc_currentadd_pincode: string;
		rc_non_use: string;
		rc_tax_mode: string;
	};
	violations: IChallanViolationDetails[];
	puc_expired: boolean;
	evidence_image_count: number;
	total_challans: number;
}

export interface ICreateChallanRequestData {
	event_id: number;
	image_1?: string;
	image_2?: string;
}

export interface IChallanCreatedDetails {
	id: number;
	transaction_no: string;
	registration_number: string;
	status: "success" | "failed";
	offence_id: string;
	offence_name: string;
	violation_type: string;
	created_at: string;
	action_time: string;
	response_message: string;
}

export interface ICreateChallanResponse {
	success: boolean;
	message: string;
	challans: IChallanCreatedDetails[];
	stats: {
		total: number;
		issued: number;
		failed: number;
		draft: number;
		paid: number;
	};
}

export interface IUploadPlateImageRequestData {
	event_id: number;
	image: string;
}
